import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PlatformLayout } from "./PlatformLayout";
import { ListingCard } from "./ListingCard";
import { Reveal } from "./Reveal";
import { AnimatedTitle } from "./AnimatedTitle";
import { PortalBanner } from "./PortalBanner";
import { fetchListings, fetchFacets, SITE_DEFAULT_COVERS } from "./api";
import { logSearch } from "./analytics";
import { Search, X, RotateCcw, SlidersHorizontal, ChevronDown, Mic } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import EspaceAcquereur from "./EspaceAcquereur";

const SORT_KEYS = ["featured", "price_asc", "price_desc", "date", "surface"];
const FILTER_KEYS = ["q", "type", "collection", "status", "price_min", "price_max", "surface_min", "bedrooms_min", "rooms_min", "city", "agency", "owner", "department", "region"];

const selCls = "bg-white/[0.06] border border-white/15 rounded-full px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#C19A6B] hover:border-white/30 transition-colors cursor-pointer";
const inpCls = "bg-white/[0.06] border border-white/15 rounded-full px-4 py-2.5 text-sm text-white placeholder-zinc-400 focus:outline-none focus:border-[#C19A6B] hover:border-white/30 transition-colors";

export default function Annonces() {
  const { t } = useTranslation();
  const SORTS = SORT_KEYS.map((v) => ({ v, l: t(`listings.sort_${v}`) }));
  const FILTER_LABELS = Object.fromEntries(FILTER_KEYS.map((k) => [k, t(`listings.fl_${k}`)]));
  const [params, setParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [facets, setFacets] = useState({ cities: [], advisors: [], agencies: [] });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);

  useEffect(() => { fetchFacets().then(setFacets); }, []);

  // Couverture "Nos offres" : une des plus belles annonces au hasard (photo réelle uniquement)
  const [heroCover, setHeroCover] = useState(SITE_DEFAULT_COVERS.annonces);
  useEffect(() => {
    fetchListings({ sort: "featured" }).then((d) => {
      // Préférer une image PROPRE (rendu home staging IA, sans filigrane/logo SAFTI) pour la couverture.
      const cleanImg = (l) => {
        const st = Array.isArray(l.staging) ? l.staging.find((s) => s && s.after) : null;
        if (st) return st.after;
        return l.cover || (l.images && l.images[0]);
      };
      const withImg = (d || []).filter((l) => l.cover || (l.images && l.images.length) || (Array.isArray(l.staging) && l.staging.length));
      // Priorité aux annonces disposant d'un rendu IA propre.
      const staged = withImg.filter((l) => Array.isArray(l.staging) && l.staging.some((s) => s && s.after));
      const pool = (staged.length ? staged : withImg).slice(0, 8);
      if (pool.length) {
        const p = pool[Math.floor(Math.random() * pool.length)];
        setHeroCover(cleanImg(p));
      }
    }).catch(() => {});
  }, []);

  const get = (k) => params.get(k) || "";
  const load = useCallback(() => {
    setLoading(true);
    const obj = {};
    params.forEach((v, k) => { obj[k] = v; });
    if (!obj.sort) obj.sort = "featured";
    fetchListings(obj).then((d) => { setListings(d); setLoading(false); });
  }, [params]);

  useEffect(() => { load(); }, [load]);

  // Journalisation des recherches (analytics) — anti-spam 1,2s
  useEffect(() => {
    const obj = {};
    params.forEach((v, k) => { obj[k] = v; });
    const t = setTimeout(() => logSearch(obj), 1200);
    return () => clearTimeout(t);
  }, [params]);

  const update = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };
  const reset = () => setParams(new URLSearchParams(), { replace: true });

  const activeChips = [...params.entries()].filter(([k, v]) => v && k !== "sort" && FILTER_LABELS[k]);
  const chipValue = (k, v) => {
    if (k === "owner") return facets.advisors.find((a) => a.member_id === v)?.display_name || v;
    return v;
  };

  return (
    <PlatformLayout searchDock={false}>
      <PortalBanner
        image={heroCover}
        subtitle={t("listings.hero_subtitle")}
        title={t("listings.hero_title")}
        testid="annonces-hero"
        subtitleWide
      />
      <div className="bg-zinc-100">
      <div className="pb-20 max-w-[1500px] mx-auto px-6 lg:px-12">
        {/* Bandeau de filtres horizontal — à cheval sur le bas de la photo */}
        <div className="sticky top-20 z-40 mb-6 -mt-10 sm:-mt-14 bg-[#0d0d10] backdrop-blur-xl border border-white/10 rounded-[5px] p-3 sm:p-4 shadow-2xl" data-testid="filters-bar">
          {/* Bouton mobile plier/déplier les filtres */}
          <button
            onClick={() => setFiltersOpen((v) => !v)}
            data-testid="filters-toggle"
            className="sm:hidden w-full flex items-center justify-between gap-3 text-sm text-white font-medium px-1 py-1.5"
          >
            <span className="inline-flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4 text-[#C19A6B]" /> {t("listings.filters")}
              {activeChips.length > 0 && <span className="text-[10px] bg-[#C19A6B] text-white rounded-full px-1.5 py-0.5">{activeChips.length}</span>}
            </span>
            <ChevronDown className={`w-4 h-4 text-[#C19A6B] transition-transform duration-300 ${filtersOpen ? "rotate-180" : ""}`} />
          </button>
          <div className={`overflow-hidden transition-all duration-300 ease-in-out sm:!max-h-none sm:!opacity-100 sm:!mt-0 ${filtersOpen ? "max-h-[900px] opacity-100 mt-3" : "max-h-0 opacity-0 sm:opacity-100"}`}>
          <div className="flex flex-wrap items-center gap-2.5">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input value={get("q")} onChange={(e) => update("q", e.target.value)} placeholder={t("listings.ph_keyword")} data-testid="filter-q" className={`${inpCls} w-full pl-11`} />
            </div>
            <select value={get("type")} onChange={(e) => update("type", e.target.value)} data-testid="filter-type" className={selCls}>
              <option value="">{t("listings.type")}</option><option value="Appartement">{t("search.apartment")}</option><option value="Maison">{t("search.house")}</option>
            </select>
            <select value={get("collection")} onChange={(e) => update("collection", e.target.value)} data-testid="filter-collection" className={selCls}>
              <option value="">{t("listings.collection")}</option>{["Habitat", "Signature", "Prestige"].map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <select value={get("status")} onChange={(e) => update("status", e.target.value)} data-testid="filter-status" className={selCls}>
              <option value="">{t("listings.status")}</option><option value="Disponible">{t("listings.opt_available")}</option><option value="Sous compromis">{t("listings.opt_under_offer")}</option><option value="Vendu">{t("listings.opt_sold")}</option>
            </select>
            <select value={get("sort") || "featured"} onChange={(e) => update("sort", e.target.value)} data-testid="sort-select" className={selCls}>
              {SORTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
            <button onClick={() => setMoreOpen((v) => !v)} data-testid="filters-more" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-white hover:text-[#C19A6B] border border-white/15 hover:border-[#C19A6B]/50 rounded-full px-4 py-2.5 transition-colors">
              <SlidersHorizontal className="w-3.5 h-3.5" /> {moreOpen ? t("listings.less") : t("listings.more")}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${moreOpen ? "rotate-180" : ""}`} />
            </button>
            <button onClick={() => setVoiceOpen(true)} data-testid="voice-search-open" className="inline-flex items-center gap-1.5 shrink-0 text-xs font-bold uppercase tracking-wider bg-[#C19A6B] hover:bg-[#d1af84] text-white rounded-full px-4 py-2.5 whitespace-nowrap transition-colors">
              <Mic className="w-3.5 h-3.5" /> {t("listings.voice")}
            </button>
            <div className="flex-1" />
            {activeChips.length > 0 && (
              <button onClick={reset} data-testid="filters-reset" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-white hover:text-[#C19A6B] border border-white/10 hover:border-[#C19A6B]/50 rounded-full px-4 py-2.5 transition-colors"><RotateCcw className="w-3.5 h-3.5" /> {t("listings.reset")}</button>
            )}
          </div>
          {/* 2ème ligne de filtres — dépliable */}
          <div className={`${moreOpen ? "flex" : "hidden"} flex-wrap items-center gap-2.5 mt-2.5`} data-testid="filters-secondary">
            {facets.cities.length > 0 && (
              <select value={get("city")} onChange={(e) => update("city", e.target.value)} data-testid="filter-city" className={selCls}>
                <option value="">{t("listings.fl_city")}</option>{facets.cities.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
            {(facets.departments || []).length > 1 && (
              <select value={get("department")} onChange={(e) => update("department", e.target.value)} data-testid="filter-department" className={selCls}>
                <option value="">{t("listings.fl_department")}</option>{facets.departments.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
            {(facets.regions || []).length > 1 && (
              <select value={get("region")} onChange={(e) => update("region", e.target.value)} data-testid="filter-region" className={selCls}>
                <option value="">{t("listings.fl_region")}</option>{facets.regions.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
            {facets.advisors.length > 1 && (
              <select value={get("owner")} onChange={(e) => update("owner", e.target.value)} data-testid="filter-advisor" className={selCls}>
                <option value="">{t("listings.fl_owner")}</option>{facets.advisors.map((a) => <option key={a.member_id} value={a.member_id}>{a.display_name}</option>)}
              </select>
            )}
            {facets.agencies.length > 1 && (
              <select value={get("agency")} onChange={(e) => update("agency", e.target.value)} data-testid="filter-agency" className={selCls}>
                <option value="">{t("listings.fl_agency")}</option>{facets.agencies.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
            <input value={get("price_min")} type="number" onChange={(e) => update("price_min", e.target.value)} placeholder={t("listings.ph_price_min")} data-testid="filter-price_min" className={`${inpCls} w-28`} />
            <input value={get("price_max")} type="number" onChange={(e) => update("price_max", e.target.value)} placeholder={t("listings.ph_price_max")} data-testid="filter-price_max" className={`${inpCls} w-28`} />
            <input value={get("surface_min")} type="number" onChange={(e) => update("surface_min", e.target.value)} placeholder={t("listings.ph_surface")} data-testid="filter-surface_min" className={`${inpCls} w-28`} />
          </div>
          {activeChips.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-white/10" data-testid="active-chips">
              {activeChips.map(([k, v]) => (
                <button key={k} onClick={() => update(k, "")} data-testid={`chip-${k}`} className="inline-flex items-center gap-1.5 text-xs bg-[#C19A6B]/15 text-[#C19A6B] hover:bg-[#C19A6B]/25 rounded-full pl-3 pr-2 py-1.5 transition-colors">
                  <span className="opacity-70">{FILTER_LABELS[k]} :</span> {chipValue(k, v)} <X className="w-3.5 h-3.5" />
                </button>
              ))}
            </div>
          )}
          </div>
        </div>

        <div className="flex items-center justify-between mb-5">
          <span className="calli text-2xl sm:text-3xl text-[#C19A6B] leading-none" data-testid="results-count">{loading ? t("common.loading") : `${listings.length} ${t("listings.available_word")}`}</span>
        </div>

        {!loading && listings.length === 0 ? (
          <div className="py-24 text-center text-zinc-500" data-testid="no-results">{t("listings.no_results")}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="results-grid">
            {listings.map((l, i) => <ListingCard key={l.slug} listing={l} index={i} recency />)}
          </div>
        )}
      </div>
      </div>

      {voiceOpen && createPortal(
        <AnimatePresence>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto bg-black/70 backdrop-blur-sm p-4 sm:p-8" data-testid="voice-search-modal" onClick={() => setVoiceOpen(false)}>
            <motion.div initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.97 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }} className="relative w-full max-w-5xl my-auto" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setVoiceOpen(false)} data-testid="voice-search-close" className="absolute -top-3 -right-3 z-10 w-9 h-9 rounded-full bg-white text-white shadow-lg flex items-center justify-center hover:bg-[#C19A6B] hover:text-white transition-colors"><X className="w-4 h-4" /></button>
              <EspaceAcquereur brand="OFF MARKETTE" />
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </PlatformLayout>
  );
}
